import type { Booking } from '@/types/booking';
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { CalendarIcon } from 'lucide-react';
import { TimeSelect } from '@/components/ui/time-select';
import { useBookings } from '@/contexts/BookingContext';
import { validateBookingTime } from '@/lib/booking-utils';
import { useToast } from '@/hooks/use-toast';
import { ConflictWarningDialog } from './ConflictWarningDialog';

interface DuplicateBookingDialogProps {
  booking: Booking;
  onClose: () => void;
  onDuplicate?: (date: Date) => void;
}

export function DuplicateBookingDialog({ 
  booking, 
  onClose,
  onDuplicate 
}: DuplicateBookingDialogProps) {
  const { checkDuplicate } = useBookings();
  const { toast } = useToast();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [showConflictWarning, setShowConflictWarning] = useState(false);
  const [conflictBooking, setConflictBooking] = useState<Booking | null>(null);

  const [form, setForm] = useState({
    date: new Date(),
    timeSlot: booking.timeSlot,
    copyTags: true
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newBooking: Booking = {
      ...booking,
      id: Date.now(),
      date: format(form.date, 'yyyy-MM-dd'),
      timeSlot: form.timeSlot
    };

    // Validate booking time
    const timeError = validateBookingTime(newBooking);
    if (timeError) {
      toast({
        title: "Invalid Time",
        description: timeError,
        variant: "destructive"
      });
      return;
    }

    // Check for conflicts
    const conflict = checkDuplicate(newBooking);
    if (conflict) {
      setConflictBooking(newBooking);
      setShowConflictWarning(true);
      return;
    }

    const options = {
      maintainRelationship: false,
      copyTags: form.copyTags,
      syncFields: {
        status: false,
        amount: false,
        notes: false,
        duration: false
      }
    };

    try {
      // Call the duplicateBooking function from context
      duplicateBooking(
        booking,
        format(form.date, 'yyyy-MM-dd'),
        form.timeSlot,
        options
      );

      // Call onDuplicate with the new date if provided
      onDuplicate?.(form.date);
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to duplicate booking",
        variant: "destructive"
      });
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setForm(prev => ({ ...prev, date }));
      setCalendarOpen(false);
    }
  };

  const handleAlternativeSelect = (newDate: string, newTime: string) => {
    if (conflictBooking) {
      const options = {
        maintainRelationship: false,
        copyTags: form.copyTags,
        syncFields: {
          status: false,
          amount: false,
          notes: false,
          duration: false
        }
      };

      try {
        duplicateBooking(booking, newDate, newTime, options);
        onDuplicate?.(new Date(newDate));
        onClose();
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to duplicate booking",
          variant: "destructive"
        });
      }
    }
  };

  return (
    <>
      <Dialog open modal onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Duplicate Booking</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              {/* Original Booking Info */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="text-sm font-medium text-gray-700">Original Booking:</div>
                <div className="text-sm space-y-1">
                  <div>Date: {format(new Date(booking.date), 'PPP')}</div>
                  <div>Time: {booking.timeSlot}</div>
                  <div>Duration: {booking.duration}h</div>
                  <div>Client: {booking.clientName}</div>
                </div>
              </div>

              {/* New Date & Time */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>New Date</Label>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !form.date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {form.date ? format(form.date, 'PPP') : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.date}
                        onSelect={handleDateSelect}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>New Time</Label>
                  <TimeSelect
                    value={form.timeSlot}
                    onChange={(value) => setForm(prev => ({ ...prev, timeSlot: value }))}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                className="bg-purple-600 hover:bg-purple-700"
              >
                Duplicate Booking
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {showConflictWarning && conflictBooking && (
        <ConflictWarningDialog
          conflict={checkDuplicate(conflictBooking)!}
          rooms={[{ id: booking.roomId, name: 'Room', capacity: 0, color: '' }]}
          open={showConflictWarning}
          onClose={() => setShowConflictWarning(false)}
          onSelectAlternative={handleAlternativeSelect}
        />
      )}
    </>
  );
}